// frontend/app/components/lessonPlans/LessonAuthorFilter.js
//
// Narrows the lessons-index list by who wrote it: All, Mine, Library as one
// tap each, and a specific colleague one tap further behind "By teacher".
//
// PAGE LAYOUT ONLY. The lessons index is the one screen with enough lessons,
// and enough other authors, for "who wrote this" to be worth asking; a
// dashboard column has neither. `LessonPlanLauncher` is the one that decides
// that, by rendering this only in page layout — this file has no opinion.
//
// STATE LIVES HERE, NOT IN THE CALLER. `{mode, authorId, authorName}` is
// small, screen-local UI state with no reason to travel through the rest of
// the app, and it is exactly the shape sessionStorage holds it in — a teacher
// who filtered to "Mine" and refreshed the page should not have to filter
// again. `onChange` still fires, including once on mount, so the launcher can
// compute the filtered list; ownership of the *value* stays here.
//
// `authorName` rides on the filter object alongside `authorId` so the
// launcher can write "No lessons by Tara Nguyen yet" without a second lookup
// against a list it may not have filtered the same way.

import React, { useEffect, useMemo, useState } from "react";
import { Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "./lessonPlanTheme";
import HeaderDropdown, { HeaderMenuItem } from "../header/HeaderDropdown";
import { authorOptions } from "./lessonAuthorFilterRules";

const STORAGE_KEY = "lessonAuthorFilter";

export const DEFAULT_AUTHOR_FILTER = { mode: "all", authorId: null, authorName: null };

const VALID_MODES = ["all", "mine", "author"];

/** Read the filter a teacher left the page in. Web only; never throws. */
const readStoredFilter = () => {
  if (Platform.OS !== "web" || typeof window === "undefined" || !window.sessionStorage) {
    return DEFAULT_AUTHOR_FILTER;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_AUTHOR_FILTER;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.mode !== "string") return DEFAULT_AUTHOR_FILTER;
    // A mode an earlier build persisted (e.g. the retired "library" segment)
    // is not one this build knows how to filter by — fall back rather than
    // carry a mode the UI no longer offers a way to select or clear.
    if (!VALID_MODES.includes(parsed.mode)) return DEFAULT_AUTHOR_FILTER;
    return { ...DEFAULT_AUTHOR_FILTER, ...parsed };
  } catch {
    return DEFAULT_AUTHOR_FILTER;
  }
};

const writeStoredFilter = (filter) => {
  if (Platform.OS !== "web" || typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // Private-mode Safari and similar throw on write. Losing the memory of a
    // filter choice is not worth surfacing to a teacher mid-lesson-search.
  }
};

const SEGMENTS = [
  { mode: "all", label: "All" },
  { mode: "mine", label: "Mine" },
];

const LessonAuthorFilter = ({
  // The whole (unfiltered) board list — this control needs to see what the
  // filter would hide, to decide which of its own segments are worth
  // offering. See lessonAuthorFilter.js for the counting itself.
  lessons = [],
  isDarkMode = false,
  // `(filter) => void`. Fired once on mount with the restored (or default)
  // filter, and again on every change — the launcher owns nothing of this
  // state itself, it only reacts to it.
  onChange,
  t = (value) => value,
}) => {
  const translate = t;
  const colors = getLessonColors(isDarkMode);
  const [filter, setFilter] = useState(readStoredFilter);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasMine = useMemo(() => lessons.some((lesson) => lesson?.is_mine === true), [lessons]);
  const teachers = useMemo(() => authorOptions(lessons), [lessons]);

  useEffect(() => {
    writeStoredFilter(filter);
    if (typeof onChange === "function") onChange(filter);
    // Only `filter` itself should re-fire this — `onChange` is expected to be
    // a fresh inline function on every render, and `lessons`/`t` changing
    // does not mean the filter changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // A restored `mode: "author"` filter can name someone who no longer has a
  // row in `teachers` — the board changed underneath the stored choice, or
  // (rarely) the list just hasn't loaded yet. Reset to All once the board is
  // actually in hand and still doesn't carry that author; `filter.mode` only
  // ever equals "author" here, which never matches `DEFAULT_AUTHOR_FILTER`'s
  // "all", so this can set state at most once per stale id — it cannot loop.
  useEffect(() => {
    if (lessons.length === 0) return;
    if (filter.mode !== "author") return;
    const stillPresent = teachers.some((option) => option.author_id === filter.authorId);
    if (stillPresent) return;
    setFilter(DEFAULT_AUTHOR_FILTER);
  }, [lessons.length, teachers, filter]);

  const choose = (next) => {
    setFilter(next);
    setMenuOpen(false);
  };

  const segments = SEGMENTS.filter((segment) => {
    if (segment.mode === "mine") return hasMine;
    return true;
  });

  return (
    <View testID="lesson-author-filter" style={styles.wrap}>
      {segments.map((segment) => {
        const selected = filter.mode === segment.mode;
        return (
          <TouchableOpacity
            key={segment.mode}
            testID={`lesson-author-filter-${segment.mode}`}
            onPress={() => choose({ mode: segment.mode, authorId: null, authorName: null })}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={translate(segment.label)}
            style={[
              styles.segment,
              {
                backgroundColor: selected ? colors.accentGlow : "transparent",
                borderColor: selected ? colors.accentPrimary : colors.border,
              },
            ]}
          >
            <Text
              style={[
                styles.segmentText,
                { color: selected ? colors.accentPrimary : colors.textSecondary },
              ]}
            >
              {translate(segment.label)}
            </Text>
          </TouchableOpacity>
        );
      })}

      <HeaderDropdown
        testID="lesson-author-filter-teacher"
        isDarkMode={isDarkMode}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        align="left"
        width={240}
        trigger={
          <TouchableOpacity
            testID="lesson-author-filter-teacher-trigger"
            onPress={() => setMenuOpen((value) => !value)}
            accessibilityRole="button"
            accessibilityState={{ selected: filter.mode === "author", expanded: menuOpen }}
            accessibilityLabel={translate("By teacher")}
            style={[
              styles.segment,
              {
                backgroundColor: filter.mode === "author" ? colors.accentGlow : "transparent",
                borderColor: filter.mode === "author" ? colors.accentPrimary : colors.border,
              },
            ]}
          >
            <Text
              style={[
                styles.segmentText,
                { color: filter.mode === "author" ? colors.accentPrimary : colors.textSecondary },
              ]}
              numberOfLines={1}
            >
              {filter.mode === "author" && filter.authorName
                ? filter.authorName
                : translate("By teacher")}
            </Text>
            <MaterialCommunityIcons
              name={menuOpen ? "chevron-up" : "chevron-down"}
              size={14}
              color={filter.mode === "author" ? colors.accentPrimary : colors.textSecondary}
            />
          </TouchableOpacity>
        }
      >
        {teachers.length === 0 ? (
          <HeaderMenuItem
            testID="lesson-author-filter-teacher-empty"
            isDarkMode={isDarkMode}
            label={translate("No other teachers have shared lessons yet")}
            disabled
          />
        ) : (
          teachers.map((option) => {
            const selected = filter.mode === "author" && filter.authorId === option.author_id;
            return (
              <HeaderMenuItem
                key={option.author_id}
                testID={`lesson-author-filter-teacher-option-${option.author_id}`}
                isDarkMode={isDarkMode}
                label={option.author_name}
                onPress={() =>
                  choose({
                    mode: "author",
                    authorId: option.author_id,
                    authorName: option.author_name,
                  })
                }
                right={
                  <View style={styles.optionRight}>
                    <Text style={[styles.optionCount, { color: colors.textMuted }]}>
                      {option.count}
                    </Text>
                    {selected ? (
                      <MaterialCommunityIcons
                        name="check"
                        size={16}
                        color={colors.accentPrimary}
                      />
                    ) : null}
                  </View>
                }
              />
            );
          })
        )}
      </HeaderDropdown>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
    // react-native-web gives every View its own stacking context
    // (`position: relative; z-index: 0`). Without this, the open "By
    // teacher" menu — itself stacked correctly inside this row — still
    // loses to `lesson-finder-box`, a later sibling of this row at the same
    // z-index 0. Lifting the whole row above its siblings carries the menu
    // with it.
    zIndex: 20,
  },
  segment: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  segmentText: { fontSize: 12, fontWeight: "700" },
  optionRight: { flexDirection: "row", alignItems: "center", gap: 6 },
  optionCount: { fontSize: 11, fontWeight: "700" },
});

export default LessonAuthorFilter;
